/**
 * MediaCell.js
 *
 * Inline media preview for table cells. Detects whether a cell value (or a
 * paired URL value from a sibling column) points at a Tier 1 media file and
 * renders an icon + filename that opens a lightbox modal on click — keeping
 * the user inside DataQuad instead of bouncing them to a new tab.
 *
 * Supported media (Tier 1 — natively rendered, zero deps):
 *   Images: jpg, jpeg, png, gif, webp, svg
 *   Audio:  mp3, m4a, wav, ogg, aac
 *   Video:  mp4, webm
 *   PDF:    pdf
 *
 * Anything else that's a URL renders as a plain external link with an icon.
 * Anything else falls back to plain text.
 *
 * Usage (DataPreview-style call site):
 *   const urlPairs = useMemo(() => detectMediaColumnPairs(columns), [columns]);
 *   ...
 *   <MediaCell value={row[col]} urlValue={row[urlPairs[col]]} />
 *
 * Auth: KoBo / SurveyCTO attachment URLs typically require an authenticated
 * session. If the user's browser already has a KoBo cookie / SCTO basic-auth
 * token, the <img>/<audio>/<video> elements load directly. If not, KoBo
 * returns its login page — we trap the resulting onerror and surface a
 * "couldn't load — open externally" fallback in the lightbox so the user
 * isn't stuck staring at a broken icon.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import {
  Image as ImageIcon,
  Volume2,
  Video as VideoIcon,
  FileText,
  ExternalLink,
  X,
  Download,
  Loader2
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────
// Constants — extension → renderer-type map. Adding a new format is one line.
// ─────────────────────────────────────────────────────────────────────────
const MEDIA_TYPES = {
  // Images
  jpg:  'image', jpeg: 'image', png:  'image',
  gif:  'image', webp: 'image', svg:  'image',
  // Audio
  mp3:  'audio', m4a:  'audio', wav:  'audio',
  ogg:  'audio', aac:  'audio',
  // Video — only mp4 / webm work cross-platform reliably
  mp4:  'video', webm: 'video',
  // Documents
  pdf:  'pdf',
};

// Per-type visual config — icon + accent color used in cell + lightbox header
const TYPE_VISUAL = {
  image: { Icon: ImageIcon, color: '#10B981' },  // green
  audio: { Icon: Volume2,   color: '#8B5CF6' },  // purple
  video: { Icon: VideoIcon, color: '#EC4899' },  // pink
  pdf:   { Icon: FileText,  color: '#EF4444' },  // red
};

// ─────────────────────────────────────────────────────────────────────────
// Helpers — exported for use in tests / other call sites
// ─────────────────────────────────────────────────────────────────────────

/**
 * Extract the file extension from a filename or URL.
 * Handles query strings, fragments, multi-dot filenames, leading/trailing
 * whitespace. Returns lowercase extension without the dot, or '' if none.
 *
 * Examples:
 *   getExtension('boat.JPG')                        → 'jpg'
 *   getExtension('WhatsApp Video at 3.09.26.mp4')   → 'mp4'
 *   getExtension('https://kf.kbt.org/file.png?v=2') → 'png'
 *   getExtension('https://kf.kbt.org/attachments/abc123/')  → ''  (KoBo-style)
 *   getExtension(null)                              → ''
 */
export const getExtension = (str) => {
  if (typeof str !== 'string') return '';
  const cleaned = str.split('?')[0].split('#')[0].trim();
  if (!cleaned) return '';
  const lastSlash = cleaned.lastIndexOf('/');
  const filename = lastSlash >= 0 ? cleaned.slice(lastSlash + 1) : cleaned;
  if (!filename || !filename.includes('.')) return '';
  const parts = filename.split('.');
  const ext = parts[parts.length - 1].toLowerCase().trim();
  // Real file extensions are short and alphanumeric. Cap at 5 chars to reject
  // bogus matches in things like 'Plot 31, House.no 4' or 'file.toolong'.
  // Must start with a letter (rejects '.123' chunked-filename patterns).
  if (!ext || ext.length > 5 || !/^[a-z][a-z0-9]*$/.test(ext)) return '';
  return ext;
};

/**
 * Loose URL detector. We only accept http/https — file:// and data: are
 * intentionally excluded (security + no use case in this context).
 */
export const isUrl = (str) => {
  if (typeof str !== 'string') return false;
  return /^https?:\/\//i.test(str.trim());
};

/**
 * Detect what (if any) media we can render for this cell.
 * Returns { url, type, displayName, ext }:
 *   - url:         the URL to load (from urlValue or value, whichever is a URL)
 *   - type:        one of 'image' | 'audio' | 'video' | 'pdf' | null
 *   - displayName: the filename to display (preferring `value` over URL path)
 *   - ext:         the detected extension, or ''
 *
 * Detection rules (in priority order):
 *   1. urlValue is a URL  →  use it as the source. Get extension from `value`
 *      first (KoBo URLs lack extensions; the paired filename column has them).
 *      Fall back to URL-derived extension if value has none.
 *   2. value is a URL     →  use it as the source. Extension comes from URL.
 *   3. otherwise          →  not media, return type=null.
 *
 * @param {string[]} excludeTypes  Optional. Types in this list are forced to
 *                                 type=null so they fall through to the
 *                                 "URL but not media" external-link branch.
 *                                 Useful when the call site wants different
 *                                 handling (e.g. DataPreview already has its
 *                                 own InlineAudioCell for local audio files).
 */
export const detectMedia = (value, urlValue, excludeTypes = []) => {
  const valueStr = (value === null || value === undefined) ? '' : String(value);
  const urlStr   = (urlValue === null || urlValue === undefined) ? '' : String(urlValue);

  let url = null;
  if (isUrl(urlStr))        url = urlStr.trim();
  else if (isUrl(valueStr)) url = valueStr.trim();

  // Extension: prefer value-side filename (KoBo URLs lack extensions), else URL
  let ext = getExtension(valueStr);
  if (!ext && url) ext = getExtension(url);

  let type = MEDIA_TYPES[ext] || null;
  // Suppress excluded types — caller wants this rendered as a plain link instead
  if (type && Array.isArray(excludeTypes) && excludeTypes.includes(type)) {
    type = null;
  }

  // Display name: prefer the original value when it looks like a filename,
  // else derive from URL path.
  let displayName = valueStr;
  if (!displayName && url) {
    try {
      const u = new URL(url);
      const segments = u.pathname.split('/').filter(Boolean);
      displayName = segments[segments.length - 1] || u.hostname;
    } catch { displayName = url; }
  }

  return { url, type, displayName, ext };
};

/**
 * Given a list of column names, find filename↔URL pairs using the standard
 * KoBo / SurveyCTO conventions: `<col>_URL`, `<col>_url`, `<col>URL`.
 *
 * Returns a map: { filenameCol: urlCol }. Used at render time so MediaCell
 * can look up the paired URL for each cell without scanning columns repeatedly.
 *
 * Example:
 *   detectMediaColumnPairs([
 *     'q1', 'evidence', 'evidence_URL', 'photo', 'photo_url'
 *   ])
 *   →  { evidence: 'evidence_URL', photo: 'photo_url' }
 */
export const detectMediaColumnPairs = (columns) => {
  if (!Array.isArray(columns)) return {};
  const colSet = new Set(columns);
  const pairs = {};
  const SUFFIXES = ['_URL', '_url', 'URL'];
  for (const col of columns) {
    if (typeof col !== 'string') continue;
    // Skip URL-side columns themselves so we don't return them as keys
    if (col.endsWith('_URL') || col.endsWith('_url') || col.endsWith('URL')) continue;
    for (const suffix of SUFFIXES) {
      const candidate = col + suffix;
      if (colSet.has(candidate)) {
        pairs[col] = candidate;
        break;
      }
    }
  }
  return pairs;
};

// ─────────────────────────────────────────────────────────────────────────
// Auth-host detection — when a fetch fails AND the URL is from a known
// auth-required host (KoBo / SurveyCTO), we skip the doomed direct-iframe
// attempt and go straight to the file-info card. Avoids the blank-iframe
// state KoBo PDFs were producing.
// ─────────────────────────────────────────────────────────────────────────
const AUTH_REQUIRED_HOST_PATTERNS = [
  /(^|\.)kobotoolbox\.org$/i,
  /(^|\.)surveycto\.com$/i,
  /(^|\.)surveycto\.io$/i,
];

const urlLooksAuthRequired = (url) => {
  try {
    const u = new URL(url);
    return AUTH_REQUIRED_HOST_PATTERNS.some(rx => rx.test(u.hostname));
  } catch {
    return false;
  }
};

// ─────────────────────────────────────────────────────────────────────────
// Proxy URL builder — when DataQuad has a stored connection for the URL's
// host, route the fetch through our backend proxy. This server-side fetch
// uses the user's stored KoBo / SCTO credentials, strips Content-Disposition,
// and forwards Range headers — solving the cross-origin auth + headers
// problems that prevent direct in-browser rendering of authenticated
// attachments.
//
// Only applies when:
//   - projectId is provided (we need to know which project's creds to use)
//   - URL is from a known auth-required host (KoBo / SCTO patterns)
//   - We're in a browser context with an auth token
// ─────────────────────────────────────────────────────────────────────────
const API_BASE_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
  || 'http://localhost:8000';

const buildProxyUrl = (rawUrl, projectId) => {
  if (!projectId || !rawUrl) return null;
  if (!urlLooksAuthRequired(rawUrl)) return null;
  return `${API_BASE_URL}/api/projects/${encodeURIComponent(projectId)}/media-proxy?url=${encodeURIComponent(rawUrl)}`;
};

const getAuthToken = () => {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem('token')) || null;
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────────────────────────────────
// MediaLightbox — modal renderer for full-size preview
// ─────────────────────────────────────────────────────────────────────────

const MediaLightbox = ({ url, localPath, tauriApi, type, displayName, isDarkMode, onClose, projectId }) => {
  const [loadError, setLoadError] = useState(false);
  // Blob-fetch state: for PDF and video, we try to fetch the URL (via the
  // proxy if available, else direct) with credentials and create a blob URL.
  // This bypasses Content-Disposition: attachment headers (which KoBo /
  // SurveyCTO send for these file types and which prevent inline rendering
  // in iframe / video elements).
  // Image and audio skip this — they're either not affected by the header
  // (img) or intentionally deferred (audio Q3 isn't in scope yet).
  const [blobUrl, setBlobUrl] = useState(null);
  // Initial "attempted" state: true (skip the loader) only when we're not
  // going to fetch at all — i.e. image/audio without a proxy. Other cases
  // start in attempted=false and the effect below flips it once done.
  const [blobAttempted, setBlobAttempted] = useState(() => {
    const willFetch =
      !!localPath || !!buildProxyUrl(url, projectId) || type === 'pdf' || type === 'video';
    return !willFetch;
  });
  const [blobFailed, setBlobFailed] = useState(false);
  const contentRef = useRef(null);

  // Proxy URL (when applicable) — used both for blob fetch AND as direct
  // src for image / audio when the upstream needs auth.
  const proxyUrl = buildProxyUrl(url, projectId);

  // Will we attempt a blob fetch?
  //   - Local file via Tauri → always (browser can't open arbitrary local paths)
  //   - Proxy is in play (need auth header → can't use direct src) → always
  //   - Otherwise, only for PDF/video (which break inline due to Content-
  //     Disposition); image/audio render fine from direct URL via cookies.
  const willBlobFetch = !!localPath || !!proxyUrl || type === 'pdf' || type === 'video';

  // ESC to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Body scroll lock while modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Blob fetch — primary path when proxy is configured, fallback path
  // for direct PDF/video CORS attempts. The blob URL is same-origin so
  // it has no Content-Disposition issue and can be used as a direct src.
  useEffect(() => {
    if (!willBlobFetch) {
      setBlobAttempted(true);
      return;
    }

    let cancelled = false;
    let createdUrl = null;

    // Local file via Tauri — read bytes from disk, wrap in a Blob, hand
    // out an object URL. Same downstream flow as HTTP blob fetch.
    if (localPath && tauriApi?.invoke) {
      tauriApi.invoke('read_file_bytes', { path: localPath })
        .then(bytes => {
          if (cancelled) return;
          // bytes is a Vec<u8> from Rust → arrives as number[] / Uint8Array
          const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
          // Best-effort MIME — img/audio/video don't require it, PDF does.
          // Browsers sniff most types from the Blob bytes anyway.
          const mimeByType = {
            image: 'application/octet-stream', // img tag sniffs
            video: 'video/mp4',
            audio: 'audio/mpeg',
            pdf:   'application/pdf',
          };
          const blob = new Blob([u8], { type: mimeByType[type] || 'application/octet-stream' });
          createdUrl = URL.createObjectURL(blob);
          setBlobUrl(createdUrl);
          setBlobAttempted(true);
        })
        .catch((err) => {
          if (cancelled) return;
          console.warn('Tauri read_file_bytes failed for', localPath, err);
          setBlobFailed(true);
          setBlobAttempted(true);
        });
      return () => {
        cancelled = true;
        if (createdUrl) URL.revokeObjectURL(createdUrl);
      };
    }

    // Pick fetch target + options:
    //   - Proxy available → /api/projects/.../media-proxy with Bearer token
    //   - Otherwise → direct URL with cookies (PDF/video only, may CORS-fail)
    let fetchTarget, fetchOptions;
    if (proxyUrl) {
      const token = getAuthToken();
      if (!token) {
        // No JWT in localStorage; can't authenticate to proxy. Treat as failure
        // so the file-info card surfaces (auth-required host is already true here).
        setBlobFailed(true);
        setBlobAttempted(true);
        return;
      }
      fetchTarget = proxyUrl;
      fetchOptions = { headers: { 'Authorization': `Bearer ${token}` } };
    } else {
      // PDF / video direct fetch — sends cookies, may fail CORS for KoBo.
      fetchTarget = url;
      fetchOptions = { credentials: 'include' };
    }

    fetch(fetchTarget, fetchOptions)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setBlobAttempted(true);
      })
      .catch(() => {
        // CORS / auth / network — fall through to either direct URL load
        // (for unknown hosts, may still work) or file-info card (for
        // known auth-required hosts where direct load is doomed).
        if (cancelled) return;
        setBlobFailed(true);
        setBlobAttempted(true);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [url, localPath, tauriApi, type, proxyUrl, willBlobFetch]);

  const visual = TYPE_VISUAL[type] || TYPE_VISUAL.pdf;

  const backdropStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '32px 20px',
  };

  const modalStyle = {
    backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
    borderRadius: '10px',
    border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
    maxWidth: type === 'pdf' ? '95vw' : '90vw',
    maxHeight: '92vh',
    width: type === 'pdf' ? '95vw' : 'auto',
    height: type === 'pdf' ? '92vh' : 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    // Reset inherited white-space — parent <td> sets nowrap for cell
    // truncation, but inside the modal we want body text to wrap normally.
    // Header title + buttons set nowrap explicitly where they need it.
    whiteSpace: 'normal',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 18px',
    borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
    flexShrink: 0,
  };

  const titleStyle = {
    flex: 1,
    fontSize: '13px',
    fontWeight: 500,
    color: isDarkMode ? '#f0ede4' : '#1a1a1a',
    letterSpacing: '-0.01em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const externalLinkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '5px 10px',
    borderRadius: '100px',
    border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
    color: isDarkMode ? '#a8a49a' : '#555550',
    backgroundColor: 'transparent',
    fontSize: '11px',
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  };

  const closeButtonStyle = {
    background: 'transparent',
    border: 'none',
    color: isDarkMode ? '#a8a49a' : '#6B7280',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
  };

  const contentAreaStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: type === 'pdf' ? 0 : '20px',
    overflow: 'auto',
    backgroundColor: type === 'image' ? (isDarkMode ? '#0a0a0a' : '#f5f5f5') : 'transparent',
    minHeight: type === 'audio' ? '160px' : '300px',
  };

  // Source URL: prefer blob (from successful fetch) over direct URL.
  // Blob URLs are same-origin to our app and have no Content-Disposition
  // problem, so they render reliably in iframe and video elements.
  const effectiveSrc = blobUrl || url;

  // Skip the doomed direct-URL attempt for known auth-required hosts
  // when blob-fetch failed. Avoids the blank-iframe / Content-Disposition-
  // forced-download UX. Image/audio aren't affected — they go straight
  // through their respective elements regardless.
  const skipDirectAttempt =
    blobFailed &&
    !blobUrl &&
    (type === 'pdf' || type === 'video') &&
    urlLooksAuthRequired(url);

  // Reusable file-info card shown when inline preview is unavailable.
  // Surfaces the filename + reason + two clear actions (open + download).
  const renderFileInfoCard = (reason) => {
    const Icon = visual.Icon;
    return (
      <div style={{
        textAlign: 'center',
        color: isDarkMode ? '#a8a49a' : '#6B7280',
        padding: '40px 20px',
        maxWidth: '420px',
      }}>
        <Icon size={48} style={{ color: visual.color, marginBottom: '16px', opacity: 0.85 }} />
        <p style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: isDarkMode ? '#E5E7EB' : '#222' }}>
          {displayName}
        </p>
        <p style={{ fontSize: '12px', marginBottom: '20px', lineHeight: 1.55 }}>
          {reason}
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href={url} target="_blank" rel="noopener noreferrer"
             style={{
               ...externalLinkStyle,
               backgroundColor: isDarkMode ? '#f0ede4' : '#1a1a1a',
               color: isDarkMode ? '#0D0E0E' : '#F8F5EE',
               border: 'none',
               padding: '7px 14px',
               fontSize: '12px',
             }}>
            <ExternalLink size={12} /> Open in new tab
          </a>
          <a href={url} download={displayName}
             style={{
               ...externalLinkStyle,
               padding: '7px 14px',
               fontSize: '12px',
             }}>
            <Download size={12} /> Download
          </a>
        </div>
      </div>
    );
  };

  // Type-specific content renderers ─────────────────────────────────────
  const renderContent = () => {
    // Loading state: blob fetch in progress for PDF / video. Brief — the
    // CORS error path returns in <100ms typically; success may take longer
    // depending on file size + bandwidth.
    if (!blobAttempted) {
      return (
        <div style={{ textAlign: 'center', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>
          <Loader2
            size={28}
            style={{
              color: visual.color,
              opacity: 0.85,
              marginBottom: '12px',
              animation: 'mediacell-spin 1s linear infinite',
            }}
          />
          <p style={{ fontSize: '12px' }}>Loading preview…</p>
          <style>{`@keyframes mediacell-spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
        </div>
      );
    }

    // Auth-required source where blob fetch failed → skip inline attempt
    // and surface the file-info card directly.
    if (skipDirectAttempt) {
      return renderFileInfoCard(
        "This file is hosted on an authenticated source (KoBo / SurveyCTO) and can't be previewed inline. Open it in a new tab where you may already be signed in, or download it directly."
      );
    }

    // Hard error from inline element (img/audio/video/iframe onError).
    if (loadError) {
      return renderFileInfoCard(
        "This may be because the source requires authentication. Try opening it in a new tab where you may already be signed in."
      );
    }

    if (type === 'image') {
      return (
        <img
          ref={contentRef}
          src={effectiveSrc}
          alt={displayName}
          onError={() => setLoadError(true)}
          style={{
            maxWidth: '100%',
            maxHeight: '85vh',
            objectFit: 'contain',
            borderRadius: '4px',
          }}
        />
      );
    }
    if (type === 'audio') {
      return (
        <div style={{ width: '100%', maxWidth: '500px', textAlign: 'center' }}>
          <Volume2 size={48} style={{ color: visual.color, marginBottom: '20px', opacity: 0.85 }} />
          <audio
            ref={contentRef}
            controls
            src={effectiveSrc}
            onError={() => setLoadError(true)}
            style={{ width: '100%' }}
          >
            Your browser doesn't support audio playback.
          </audio>
        </div>
      );
    }
    if (type === 'video') {
      return (
        <video
          ref={contentRef}
          controls
          src={effectiveSrc}
          onError={() => setLoadError(true)}
          style={{
            maxWidth: '100%',
            maxHeight: '85vh',
            borderRadius: '4px',
            backgroundColor: '#000',
          }}
        >
          Your browser doesn't support video playback.
        </video>
      );
    }
    if (type === 'pdf') {
      return (
        <iframe
          ref={contentRef}
          src={effectiveSrc}
          title={displayName}
          onError={() => setLoadError(true)}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
          }}
        />
      );
    }
    return null;
  };

  return (
    <div style={backdropStyle} onClick={onClose} role="dialog" aria-modal="true" aria-label={displayName}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <visual.Icon size={16} style={{ color: visual.color, flexShrink: 0 }} />
          <span style={titleStyle} title={displayName}>{displayName}</span>
          <a href={url} target="_blank" rel="noopener noreferrer" style={externalLinkStyle}
             onClick={(e) => e.stopPropagation()}>
            <ExternalLink size={11} />
            Open externally
          </a>
          <button onClick={onClose} style={closeButtonStyle} aria-label="Close" title="Close (Esc)">
            <X size={18} />
          </button>
        </div>
        <div style={contentAreaStyle}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// MediaCell — main exported component
// ─────────────────────────────────────────────────────────────────────────

/**
 * @param {*} value      The cell's primary value (filename or URL or arbitrary).
 * @param {*} urlValue   Optional paired URL from a sibling column (e.g. when
 *                       value comes from `evidence` and there's an `evidence_URL`
 *                       sibling).
 * @param {string} projectId  Optional. When provided AND the URL is from a
 *                       known auth-required host (KoBo / SCTO), the lightbox
 *                       routes its blob fetch through the backend proxy at
 *                       /api/projects/{projectId}/media-proxy, which uses the
 *                       project's stored credentials server-side. Without
 *                       projectId, the lightbox falls back to direct fetch
 *                       (CORS-permitting) or the file-info card.
 * @param {string} mediaDir  Optional. Absolute path to the project's local
 *                       media folder (set via Data Sources or Audio Audit).
 *                       When provided AND the cell value is a bare filename
 *                       (not a URL) AND we're in Tauri, the lightbox loads
 *                       the file via the Tauri filesystem API. Without it,
 *                       bare-filename media cells fall back to plain text.
 * @param {Object} tauriApi  Optional. Pre-resolved Tauri API ({invoke})
 *                       passed in by parent so we don't re-import it per
 *                       cell. DataPreview already loads this once per mount;
 *                       re-using avoids dozens of dynamic imports per row.
 * @param {string[]} excludeTypes  Optional. Suppress inline rendering for these
 *                       media types — they fall through to the external-link
 *                       branch instead. Useful when another component already
 *                       handles a type differently (e.g. DataPreview's
 *                       InlineAudioCell for local-file audio in Tauri mode,
 *                       where URL-based audio is intentionally deferred).
 * @param {boolean} isDarkMode  Optional override; otherwise read from ThemeSwitcher.
 * @param {number} maxFilenameLength  Truncate long filenames in the cell. Default 28.
 */
const MediaCell = ({
  value,
  urlValue,
  projectId,
  mediaDir,
  tauriApi,
  excludeTypes,
  isDarkMode: forceDarkMode,
  maxFilenameLength = 28,
}) => {
  const { isDarkMode: contextDarkMode } = useTheme();
  const isDarkMode = forceDarkMode !== undefined ? forceDarkMode : contextDarkMode;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const { url, type, displayName } = detectMedia(value, urlValue, excludeTypes);

  // Local-file resolution: when we have a media folder + Tauri runtime,
  // ALWAYS prefer reading from disk — even when the cell carries an HTTP
  // URL (SurveyCTO / KoBo attachments). Those URLs are auth-gated and
  // can't be previewed inline anyway; the matching file is almost always
  // present in the user's project media folder. The URL stays available
  // as a fallback via the lightbox's "Open externally" header link.
  //
  // Source for the basename:
  //   - value is a non-URL string (bare-filename column case) → use it directly
  //   - value is a URL (or empty) → strip query/fragment, take last path
  //     segment. e.g.
  //     https://igcsurvey.surveycto.com/.../attachments/1772549235861.jpg
  //     → 1772549235861.jpg
  //
  // Path separator: peek at mediaDir to decide. Windows paths usually
  // contain backslashes; everything else uses forward slash. Rust's
  // PathBuf accepts either on Windows but we mirror the user's style.
  let localPath = null;
  if (type && mediaDir && tauriApi) {
    let basename = null;
    const valueStr = typeof value === 'string' ? value.trim() : '';
    if (valueStr && !isUrl(valueStr)) {
      basename = valueStr;
    } else if (url) {
      try {
        const u = new URL(url);
        const last = u.pathname.split('/').filter(Boolean).pop();
        if (last) basename = decodeURIComponent(last);
      } catch {
        // URL parse failed — fall back to regex on the raw string
        const m = String(url).split(/[?#]/)[0].match(/[^\/\\]+$/);
        if (m && m[0]) {
          try { basename = decodeURIComponent(m[0]); }
          catch { basename = m[0]; }
        }
      }
    }
    if (basename) {
      const sep = mediaDir.includes('\\') && !mediaDir.includes('/') ? '\\' : '/';
      const cleanDir = mediaDir.replace(/[\/\\]+$/, ''); // strip trailing slashes
      localPath = cleanDir + sep + basename;
    }
  }

  // Plain text — render as-is when there's no URL AND no local path we
  // can resolve. Handles null, undefined, numbers, booleans.
  if (!url && !localPath) {
    if (value === null || value === undefined || value === '') return null;
    return <span>{String(value)}</span>;
  }

  // Truncate long filenames for compact table display
  const truncate = (s) =>
    typeof s === 'string' && s.length > maxFilenameLength
      ? s.slice(0, maxFilenameLength - 1) + '…'
      : (s || '');

  // URL but not media — external link
  if (!type) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          color: isDarkMode ? '#7DD3FC' : '#0369A1',
          textDecoration: 'none',
          fontSize: '12px',
          maxWidth: '100%',
        }}
        title={displayName}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {truncate(displayName)}
        </span>
        <ExternalLink size={11} style={{ flexShrink: 0, opacity: 0.7 }} />
      </a>
    );
  }

  // Media — icon + filename, click opens lightbox
  const visual = TYPE_VISUAL[type];
  const Icon = visual.Icon;

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
        title={`${displayName} — click to preview`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '3px 8px',
          background: 'transparent',
          border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
          borderRadius: '4px',
          fontSize: '12px',
          color: isDarkMode ? '#E5E7EB' : '#1a1a1a',
          cursor: 'pointer',
          fontFamily: 'inherit',
          maxWidth: '100%',
          letterSpacing: '-0.005em',
          transition: 'all 0.12s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isDarkMode
            ? 'rgba(255, 255, 255, 0.04)'
            : 'rgba(0, 0, 0, 0.03)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Icon size={12} style={{ color: visual.color, flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {truncate(displayName)}
        </span>
      </button>
      {lightboxOpen && (
        <MediaLightbox
          url={url}
          localPath={localPath}
          tauriApi={tauriApi}
          type={type}
          displayName={displayName}
          isDarkMode={isDarkMode}
          projectId={projectId}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

export default MediaCell;